// 最低発注数量（MOQ）を Stocky の画面から【貼り付けて】取り込む。
//
// なぜ CSV ではなく貼り付けなのか＝Stocky の MOQ は
// `Vendors → 対象ベンダー → Settings → Order limits` に一覧表示されるが、**CSV 書き出しが
// 無い**（2026-08-01 実画面で確定）。しかも画面をコピーすると `Min Order` が
// 別の行に落ちる（右寄せセルのため）:
//
//     半袖⇥Default Title⇥HANSODE-001⇥
//     5
//
// 🔴 表計算で整形させると移行の摩擦になるだけなので、**そのまま貼れる欄**を出して
// サーバ側で吸収する（解釈は backend の moq_paste_import が SKU を意味的な
// アンカーにして行う＝行の切れ方に依存しない）。
//
// 🔴 列の対応付けが無い代わりに、**確定前に「何がどう読まれたか」を必ず見せる**。
// これがこの画面の唯一の安全弁で、他の系統の「preview → 対応付け」に相当する。
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  applyMoqPaste,
  listMoqSupplierOptions,
  previewMoqPaste,
  type MoqPastePreview,
  type MoqPasteResult,
} from "../../api/stocky_csv_import";
import { ApiError } from "../../api/client";
import { QueryErrorBanner } from "../../components/QueryErrorBanner";
import { csvErrorMessage } from "../../components/csvImportSpec";
import { useAuthoritativeShop } from "../../hooks/useAuthoritativeShop";
import { currentLocale, t } from "../../lib/i18n";

interface Props {
  onImported: () => void;
}

/** 読めた件数が多いときに一覧を畳む閾値（全部出すと確認の役に立たない）。 */
const PREVIEW_LIMIT = 20;

interface DryRunSnapshot {
  /** 実適用は現在の入力値ではなく、dry-run で確認した同一スナップショットへ束縛する。 */
  inputRevision: number;
  supplierId: string;
  text: string;
  sourceImportId: string;
  result: MoqPasteResult;
}

export function MoqPasteImport(props: Props) {
  const authoritativeShop = useAuthoritativeShop();

  if (authoritativeShop.isError) {
    return (
      <QueryErrorBanner
        title={t("ストア情報を取得できませんでした")}
        onRetry={authoritativeShop.retry}
      />
    );
  }
  if (authoritativeShop.shopGeneration === null) {
    return <Text as="p">{t("ストア情報を確認しています…")}</Text>;
  }

  return (
    <MoqPasteImportSession
      key={authoritativeShop.shopGeneration}
      {...props}
      shopGeneration={authoritativeShop.shopGeneration}
    />
  );
}

function MoqPasteImportSession({
  onImported,
  shopGeneration,
}: Props & { shopGeneration: string }) {
  const inputRevisionRef = useRef(0);
  const sessionActive = useRef(true);
  const sourceImportIdRef = useRef(globalThis.crypto.randomUUID());
  const [text, setText] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [preview, setPreview] = useState<MoqPastePreview | null>(null);
  const [dryRun, setDryRun] = useState<DryRunSnapshot | null>(null);
  const [result, setResult] = useState<MoqPasteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [retentionOnlyConfirmed, setRetentionOnlyConfirmed] = useState(false);
  const suppliersQuery = useQuery({
    queryKey: [
      "stocky-migration",
      "moq-supplier-options",
      "shop-generation",
      shopGeneration,
    ],
    queryFn: listMoqSupplierOptions,
  });
  const suppliers = suppliersQuery.data ?? [];
  const selectedSupplier = suppliers.find(
    (supplier) => supplier.id === supplierId,
  );

  useEffect(() => {
    // React StrictMode は開発時に effect を setup→cleanup→setup と再実行する。
    // setup ごとに有効化し直し、実際に世代が外れた session の応答だけを捨てる。
    sessionActive.current = true;
    return () => {
      sessionActive.current = false;
      inputRevisionRef.current += 1;
    };
  }, []);

  const reset = useCallback(() => {
    setPreview(null);
    setDryRun(null);
    setResult(null);
    setError(null);
    setRetentionOnlyConfirmed(false);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      inputRevisionRef.current += 1;
      // 貼り付け文字列の変更は新しい source occurrence。確定後の通信再試行では
      // DryRunSnapshot のUUIDを使い続ける。
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setText(value);
      // 貼り直したら前の解釈と結果は捨てる（別の内容の件数を確定ボタンの横に残さない）。
      reset();
    },
    [reset],
  );

  const handleSupplierChange = useCallback(
    (value: string) => {
      inputRevisionRef.current += 1;
      // 同じ文字列でも Vendor が変われば別の Vendor×表 occurrence。
      sourceImportIdRef.current = globalThis.crypto.randomUUID();
      setSupplierId(value);
      // 仕入先 x SKU の値なので、仕入先を変えたら前の preview は無効。
      reset();
    },
    [reset],
  );

  const handlePreview = useCallback(async () => {
    const inputRevision = inputRevisionRef.current;
    setIsBusy(true);
    setError(null);
    setDryRun(null);
    try {
      const checked = await previewMoqPaste(supplierId, text);
      if (!sessionActive.current) return;
      if (inputRevisionRef.current !== inputRevision) return;
      setPreview(checked);
      setResult(null);
    } catch (err) {
      if (!sessionActive.current) return;
      if (inputRevisionRef.current !== inputRevision) return;
      setError(csvErrorMessage(err));
    } finally {
      if (sessionActive.current) setIsBusy(false);
    }
  }, [supplierId, text]);

  const handleDryRun = useCallback(async () => {
    const inputRevision = inputRevisionRef.current;
    setIsBusy(true);
    setError(null);
    setDryRun(null);
    try {
      const boundSupplierId = supplierId;
      const boundText = text;
      const boundSourceImportId = sourceImportIdRef.current;
      const checked = await applyMoqPaste(boundSupplierId, boundText, {
        dryRun: true,
        sourceImportId: boundSourceImportId,
        sourceFileName: "stocky-order-limits-paste.txt",
        sourceMediaType: "text/plain",
      });
      if (!sessionActive.current) return;
      if (
        !checked.dry_run ||
        !checked.plan_token ||
        checked.confirmed_preview === null ||
        checked.confirmed_preview.supplier_id !== boundSupplierId
      ) {
        throw new Error("dry-run response was not marked as dry-run");
      }
      if (inputRevisionRef.current !== inputRevision) return;
      // 最初のparser表示ではなく、tokenと同じShop lock内で確定した表示へ置換する。
      setPreview(checked.confirmed_preview);
      setRetentionOnlyConfirmed(false);
      setDryRun({
        inputRevision,
        supplierId: boundSupplierId,
        text: boundText,
        sourceImportId: boundSourceImportId,
        result: checked,
      });
    } catch (err) {
      if (!sessionActive.current) return;
      if (inputRevisionRef.current !== inputRevision) return;
      setError(csvErrorMessage(err));
    } finally {
      if (sessionActive.current) setIsBusy(false);
    }
  }, [supplierId, text]);

  const handleApply = useCallback(async () => {
    if (dryRun === null || dryRun.inputRevision !== inputRevisionRef.current) {
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      // 🔴 現在の state を読み直さず、ユーザーが件数確認した入力をそのまま送る。
      const applied = await applyMoqPaste(dryRun.supplierId, dryRun.text, {
        dryRun: false,
        expectedPlanToken: dryRun.result.plan_token ?? undefined,
        sourceImportId: dryRun.sourceImportId,
        sourceFileName: "stocky-order-limits-paste.txt",
        sourceMediaType: "text/plain",
        confirmRetentionOnly:
          dryRun.result.normalization_mode === "retention_only" &&
          retentionOnlyConfirmed,
      });
      if (!sessionActive.current) return;
      setResult(applied);
      onImported();
    } catch (err) {
      if (!sessionActive.current) return;
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 409 || err.status === 422)
      ) {
        // supplier/variant消失、状態差分、検証不能になった token は再利用せず、
        // parser表示から確認し直す。通信系エラーだけは同じ確認で安全に再試行できる。
        setPreview(null);
        setDryRun(null);
        setResult(null);
      }
      setError(csvErrorMessage(err));
    } finally {
      if (sessionActive.current) setIsBusy(false);
    }
  }, [dryRun, onImported, retentionOnlyConfirmed]);

  const skipped = preview?.rows.filter((row) => row.quantity === null) ?? [];
  const matched =
    preview?.rows.filter(
      (row) =>
        row.quantity !== null &&
        // dry-run後はtokenと同じwrite planに入ったvariantだけを確認一覧へ出す。
        // ambiguous/conflict/range外はquantityが読めてもID=nullなので混ぜない。
        (dryRun === null || row.shopify_variant_id !== null),
    ) ?? [];
  const resultExcluded =
    result !== null &&
    (result.invalid_row_count > 0 ||
      result.unmatched_count > 0 ||
      result.skipped_without_quantity_count > 0 ||
      result.orphan_quantity_count > 0);
  const retentionOnly = dryRun?.result.normalization_mode === "retention_only";

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">
            {t("最低発注数量（MOQ）")}
          </Text>
          <Text as="p" tone="subdued">
            {t("書き出し方")}: {t(
              "Stocky で Vendors → 対象の vendor → Settings → Order limits。表を見出しごとコピーして下へ貼り付けます。CSVへの加工は不要です。",
            )}
          </Text>
        </BlockStack>

        {suppliersQuery.isLoading && (
          <InlineStack gap="200" blockAlign="center">
            <Spinner
              size="small"
              accessibilityLabel={t("仕入先を読み込み中")}
            />
            <Text as="span" tone="subdued">
              {t("仕入先を読み込み中")}
            </Text>
          </InlineStack>
        )}

        {suppliersQuery.isError && (
          <Banner
            tone="critical"
            title={t("仕入先を取得できませんでした")}
            action={{
              content: t("再試行"),
              onAction: () => void suppliersQuery.refetch(),
            }}
          >
            <Text as="p">{t("時間をおいて、もう一度お試しください。")}</Text>
          </Banner>
        )}

        {!suppliersQuery.isLoading &&
          !suppliersQuery.isError &&
          suppliers.length === 0 && (
            <Banner tone="warning" title={t("仕入先が未登録です")}>
              <Text as="p">
                {t(
                  "この最低発注数量は仕入先ごとに保存します。先に UreyukiBox の「仕入先」で仕入先を登録してください。",
                )}
              </Text>
            </Banner>
          )}

        {suppliers.length > 0 && (
          <Select
            label={t("取り込み先の仕入先")}
            options={[
              { label: t("選択してください"), value: "" },
              ...suppliers.map((supplier) => ({
                label: supplier.label,
                value: String(supplier.id),
              })),
            ]}
            value={supplierId}
            onChange={handleSupplierChange}
            disabled={isBusy}
            helpText={t(
              "Stocky で開いた vendor と同じ仕入先を選んでください。MOQ はこの仕入先と商品の組み合わせに保存されます。",
            )}
          />
        )}

        <TextField
          label={t("貼り付け欄")}
          value={text}
          onChange={handleChange}
          multiline={6}
          autoComplete="off"
          placeholder={currentLocale() === "ja" ? "半袖\tDefault Title\tHANSODE-001\n5" : "T-Shirt\tDefault Title\tTSHIRT-001\n5"}
          helpText={t(
            "見出し行が入っていても、数量が別の行になっていても、そのままで大丈夫です。",
          )}
          disabled={isBusy}
        />

        {error && (
          <Banner tone="critical" title={t("取り込めませんでした")}>
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {preview && !result && (
          <BlockStack gap="300">
            {preview.normalization_mode === "retention_only" && (
              <Banner
                tone="warning"
                title={t(
                  "100万物理行を超えるため、運用データへは反映せず保全します",
                )}
              >
                <BlockStack gap="100">
                  <Text as="p">
                    {t(
                      "物理行数が安全上限1,000,000行を超えています。最低発注数量には1件も書き込まず、送信された正確な原本と解析可能な全行を汎用JSONLとして保持します（{rows}行すべて運用変換は未処理）。",
                      {
                        rows: preview.unprocessed_rows.toLocaleString("en-US"),
                      },
                    )}
                  </Text>
                  <Text as="p">
                    {t(
                      "保存後は、この画面の「保存済みデータの取り出し」から原本をダウンロードし、汎用JSONLを分割取得できます。",
                    )}
                  </Text>
                </BlockStack>
              </Banner>
            )}
            <Banner
              tone={
                preview.normalization_mode === "retention_only"
                  ? "warning"
                  : preview.matched_count > 0
                    ? "info"
                    : "warning"
              }
              title={
                preview.normalization_mode === "retention_only"
                  ? t("原本と解析可能な全行を保全できます")
                  : t("{count} 件の最低発注数量を読み取りました", {
                      count: preview.matched_count,
                    })
              }
            >
              <BlockStack gap="200">
                {preview.matched_count > 0 && (
                  <Text as="p">
                    {t(
                      "仕入先「{name}」の条件として取り込みます。違っていれば仕入先を選び直すか、貼り付け直してください。",
                      {
                        // 同名vendorを選び違えないよう、dry-runと同じserver labelを表示する。
                        name: preview.supplier_label,
                      },
                    )}
                  </Text>
                )}
                {preview.matched_count === 0 &&
                  preview.normalization_mode !== "retention_only" && (
                    <Text as="p">
                      {t(
                        "SKU が1件も見つかりませんでした。対象のベンダーの表を、見出しごと選択してコピーしてください。",
                      )}
                    </Text>
                  )}
              </BlockStack>
            </Banner>

            {preview.rows_truncated && (
              <Banner
                tone="info"
                title={t("表示は先頭 {limit} 件までです", {
                  limit: preview.preview_limit,
                })}
              >
                <Text as="p">
                  {t(
                    "全 {count} 件を解析済みです。取り込み時は表示されていない行も含めて全件を処理します。",
                    {
                      count: preview.total_rows,
                    },
                  )}
                </Text>
              </Banner>
            )}

            {matched.length > 0 && (
              <BlockStack gap="100">
                {matched.slice(0, PREVIEW_LIMIT).map((row, index) => (
                  <InlineStack
                    key={`${row.shopify_variant_id ?? "unresolved"}:${row.sku}:${index}`}
                    gap="200"
                    blockAlign="center"
                  >
                    <Text as="span" variant="bodySm">
                      {row.sku}
                    </Text>
                    <Badge tone="info">{String(row.quantity)}</Badge>
                  </InlineStack>
                ))}
                {preview.matched_count > PREVIEW_LIMIT && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t("ほか {count} 件", {
                      count: preview.matched_count - PREVIEW_LIMIT,
                    })}
                  </Text>
                )}
              </BlockStack>
            )}

            {/* 🔴 数量が読めなかった SKU を黙って 0 にしない＝理由として見せる。 */}
            {preview.skipped_without_quantity_count > 0 && (
              <Banner
                tone="warning"
                title={t("数量が読み取れなかった商品があります")}
              >
                <Text as="p">
                  {t(
                    "数量が見つからず取り込まない SKU は {count} 件です。表示例: {skus}",
                    {
                      count: preview.skipped_without_quantity_count,
                      skus: skipped.map((row) => row.sku).join(", "),
                    },
                  )}
                </Text>
              </Banner>
            )}

            {preview.out_of_range_count > 0 && (
              <Banner tone="warning" title={t("範囲外の数量があります")}>
                <Text as="p">
                  {t(
                    "最低発注数量は1以上で入力します。範囲外の {count} 件は取り込みません。",
                    {
                      count: preview.out_of_range_count,
                    },
                  )}
                </Text>
              </Banner>
            )}

            {preview.ambiguous_sku_count > 0 && (
              <Banner
                tone="critical"
                title={t("同じ SKU の商品が複数あります")}
              >
                <Text as="p">
                  {t(
                    "商品を一意に特定できないため、次の SKU は取り込みません: {skus}",
                    {
                      skus: preview.ambiguous_skus.join(", "),
                    },
                  )}
                </Text>
                {preview.ambiguous_skus_truncated && (
                  <Text as="p">
                    {t(
                      "表示は先頭 {shown} 件までです。該当する SKU は全 {count} 件です。",
                      {
                        shown: preview.ambiguous_skus.length,
                        count: preview.ambiguous_sku_count,
                      },
                    )}
                  </Text>
                )}
              </Banner>
            )}

            {preview.conflicting_sku_count > 0 && (
              <Banner
                tone="critical"
                title={t("同じ SKU に異なる数量があります")}
              >
                <Text as="p">
                  {t(
                    "どちらを使うか決められないため、次の SKU は取り込みません。Stocky の表を確認してください: {skus}",
                    {
                      skus: preview.conflicting_skus.join(", "),
                    },
                  )}
                </Text>
                {preview.conflicting_skus_truncated && (
                  <Text as="p">
                    {t(
                      "表示は先頭 {shown} 件までです。該当する SKU は全 {count} 件です。",
                      {
                        shown: preview.conflicting_skus.length,
                        count: preview.conflicting_sku_count,
                      },
                    )}
                  </Text>
                )}
              </Banner>
            )}

            {/* 🔴 余った数量を黙って捨てない。Stocky 側で SKU 未設定の商品があると
                「貼ったのに読まれない」が理由なしで起きる（実際に踏んだ）。 */}
            {preview.orphan_quantity_count > 0 && (
              <Banner
                tone="warning"
                title={t("どの商品にも結び付けられなかった数量があります")}
              >
                <Text as="p">
                  {t(
                    "対応する SKU が見つからない数量は {count} 件です。表示例: {values}",
                    {
                      count: preview.orphan_quantity_count,
                      values: preview.orphan_quantities.join(", "),
                    },
                  )}
                </Text>
                <Text as="p">
                  {t(
                    "Stocky 側で SKU が未設定の商品があると、その行は数量だけになり取り込めません。Shopify に SKU を設定してから貼り直してください。",
                  )}
                </Text>
              </Banner>
            )}

            {preview.unknown_sku_count > 0 && (
              <Banner
                tone="warning"
                title={t("このストアに無い SKU があります")}
              >
                <Text as="p">
                  {t(
                    "商品が見つからない SKU は {count} 件です（同期がまだか、SKU が違う可能性があります）。表示例: {skus}",
                    {
                      count: preview.unknown_sku_count,
                      skus: preview.unknown_skus.join(", "),
                    },
                  )}
                </Text>
              </Banner>
            )}
          </BlockStack>
        )}

        {result && result.normalization_mode === "retention_only" && (
          <Banner
            tone="warning"
            title={t("原本と汎用JSONLを保存しました（最低発注数量は未反映）")}
          >
            <Text as="p">
              {t(
                "{rows}行は最低発注数量へ1件も書き込んでいません。保存済みデータの取り出しから原本をダウンロードし、解析可能な全行の汎用JSONLを分割取得できます。",
                {
                  rows: result.unprocessed_rows.toLocaleString("en-US"),
                },
              )}
            </Text>
          </Banner>
        )}

        {result && result.normalization_mode !== "retention_only" && (
          <Banner
            tone={resultExcluded ? "warning" : "success"}
            title={t(
              "仕入先「{name}」の最低発注数量を新規 {created} 件・更新 {updated} 件 取り込みました",
              {
                name: selectedSupplier?.name ?? preview?.supplier_name ?? "",
                created: result.created,
                updated: result.updated,
              },
            )}
          >
            <BlockStack gap="200">
              {resultExcluded && (
                <Text as="p">
                  {t(
                    "一部のみ取り込みました。対象外の内訳を確認してください。",
                  )}
                </Text>
              )}
              {resultExcluded && (
                <Text as="p">
                  {t(
                    "取り込まないデータ: 不正 {invalid} 件・未一致 SKU {unmatched} 件・数量なし {skipped} 件・SKU に結び付かない数量 {orphan} 件",
                    {
                      invalid: result.invalid_row_count,
                      unmatched: result.unmatched_count,
                      skipped: result.skipped_without_quantity_count,
                      orphan: result.orphan_quantity_count,
                    },
                  )}
                </Text>
              )}
              {result.unmatched_skus.length > 0 && (
                <Text as="p">
                  {t("商品が見つからなかった SKU: {skus}", {
                    skus: result.unmatched_skus.join(", "),
                  })}
                </Text>
              )}
              {result.diagnostics_truncated && (
                <Text as="p">
                  {t("詳細一覧は表示上限までです。上の件数が全体件数です。")}
                </Text>
              )}
            </BlockStack>
          </Banner>
        )}

        {dryRun && !result && retentionOnly && (
          <Banner
            tone="warning"
            title={t(
              "まだ保存していません。運用データへ書かない保全内容を確認してください",
            )}
          >
            <BlockStack gap="200">
              <Text as="p">
                {t(
                  "最低発注数量には1件も書き込まず、送信された正確な原本と解析可能な全行を汎用JSONLとして保持します（{rows}行すべて運用変換は未処理）。",
                  {
                    rows: dryRun.result.unprocessed_rows.toLocaleString(
                      "en-US",
                    ),
                  },
                )}
              </Text>
              <Checkbox
                label={t(
                  "最低発注数量へ書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保全することを確認する",
                )}
                checked={retentionOnlyConfirmed}
                onChange={setRetentionOnlyConfirmed}
                disabled={isBusy}
              />
            </BlockStack>
          </Banner>
        )}

        {dryRun && !result && !retentionOnly && (
          <Banner
            tone={dryRun.result.updated > 0 ? "warning" : "info"}
            title={
              dryRun.result.updated > 0
                ? t("既存の最低発注数量 {count} 件を上書きします", {
                    count: dryRun.result.updated,
                  })
                : t("最低発注数量を新規 {count} 件 取り込みます", {
                    count: dryRun.result.created,
                  })
            }
          >
            <BlockStack gap="200">
              <Text as="p">
                {t("新規 {created} 件・既存を上書き {updated} 件です。", {
                  created: dryRun.result.created,
                  updated: dryRun.result.updated,
                })}
              </Text>
              {(dryRun.result.invalid_row_count ?? 0) > 0 ||
              (dryRun.result.unmatched_count ?? 0) > 0 ||
              dryRun.result.skipped_without_quantity_count > 0 ||
              dryRun.result.orphan_quantity_count > 0 ? (
                <Text as="p">
                  {t(
                    "取り込まないデータ: 不正 {invalid} 件・未一致 SKU {unmatched} 件・数量なし {skipped} 件・SKU に結び付かない数量 {orphan} 件",
                    {
                      invalid: dryRun.result.invalid_row_count ?? 0,
                      unmatched: dryRun.result.unmatched_count ?? 0,
                      skipped: dryRun.result.skipped_without_quantity_count,
                      orphan: dryRun.result.orphan_quantity_count,
                    },
                  )}
                </Text>
              ) : null}
              <Text as="p">
                {t(
                  "まだ保存していません。件数と仕入先を確認してから確定してください。",
                )}
              </Text>
            </BlockStack>
          </Banner>
        )}

        <InlineStack gap="200">
          <Button
            onClick={handlePreview}
            loading={isBusy && !result}
            disabled={
              text.trim().length === 0 ||
              supplierId === "" ||
              selectedSupplier === undefined ||
              isBusy
            }
          >
            {t("読み取り内容を確認")}
          </Button>
          {preview &&
            (preview.matched_count > 0 ||
              preview.normalization_mode === "retention_only") &&
            !dryRun &&
            !result && (
              <Button onClick={handleDryRun} loading={isBusy} disabled={isBusy}>
                {t("上書き件数を確認")}
              </Button>
            )}
          {dryRun && !result && (
            <Button
              variant="primary"
              onClick={handleApply}
              loading={isBusy}
              disabled={isBusy || (retentionOnly && !retentionOnlyConfirmed)}
            >
              {retentionOnly
                ? t("原本と汎用JSONLを保存する")
                : t("この内容を確認して取り込む")}
            </Button>
          )}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
